/**
 * THE student's Supabase client, in two times (spec docs/specs/2026-10-14_student-lite-supabase-boot.md).
 * `apps/student/vite.config.ts` points `@theidiomaclub/supabase` here; the other three apps keep `client.ts`.
 * Boot: session + queries + functions (`lite-core.ts`). Realtime and storage load on first use, from a full
 * supabase-js client created in `accessToken` mode — it has no auth of its own, so there is still ONE GoTrue per
 * tab (supabase-js 2.97.0 dist/index.mjs:219-224) and it always sends this session's token.
 * Same public API as `index.ts`, so no caller changes. Anything else on the client fails loud.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import { createLiteCore, type LiteCore } from './lite-core';
import { DeferredChannel, type RealtimeSource } from './lite-realtime';
import { deferredStorage, type StorageSource } from './lite-storage';
import { SUPABASE_STORAGE_KEY } from './storage-key';

export { SUPABASE_STORAGE_KEY };
export type { Database, Json, Tables, TablesInsert, TablesUpdate } from './database.types';
export type { SupabaseClient };

interface DatabaseModuleType {
    client: SupabaseClient | null;
    _initPromise: Promise<SupabaseClient | null> | null;
    init(): Promise<SupabaseClient | null>;
}

/** Members of supabase-js's SupabaseClient that this facade does not carry: reading one is a bug to surface. */
const NOT_CARRIED: ReadonlySet<string> = new Set([
    'rest', 'realtime', 'supabaseUrl', 'supabaseKey', 'storageKey', 'authUrl', 'storageUrl', 'functionsUrl',
    'realtimeUrl', 'headers', 'accessToken', 'fetch', 'changedAccessToken',
]);

let core: LiteCore | null = null;
let full: SupabaseClient | null = null;
let fullLoading: Promise<SupabaseClient> | null = null;
let config: { readonly url: string; readonly anonKey: string } | null = null;

export function loadSupabaseExtras(): Promise<SupabaseClient> {
    if (full) return Promise.resolve(full);
    if (!core || !config) return Promise.reject(new Error('[supabase-lite] loadSupabaseExtras before waitForClient'));
    const boot = core;
    const { url, anonKey } = config;
    fullLoading ??= import('@supabase/supabase-js')
        .then(({ createClient }) => {
            const client = createClient(url, anonKey, { accessToken: boot.accessToken });
            boot.auth.onAuthStateChange(() => {
                client.realtime.setAuth().catch((error: unknown) => console.error('[supabase-lite] realtime token not updated', error));
            });
            full = client;
            return client;
        })
        .catch((error: unknown) => {
            fullLoading = null;
            throw error;
        });
    return fullLoading;
}

function facade(boot: LiteCore, url: string): SupabaseClient {
    const realtime = async (): Promise<RealtimeSource> => (await loadSupabaseExtras()) as unknown as RealtimeSource;
    const target: Record<string, unknown> = {
        auth: boot.auth,
        functions: boot.functions,
        from: (relation: string) => boot.rest.from(relation),
        schema: (schema: string) => boot.rest.schema(schema),
        rpc: (fn: string, args?: Record<string, unknown>, options?: Parameters<typeof boot.rest.rpc>[2]) => boot.rest.rpc(fn, args, options),
        storage: deferredStorage(
            url,
            async () => (await loadSupabaseExtras()).storage as unknown as StorageSource,
            () => (full ? (full.storage as unknown as StorageSource) : null),
        ),
        channel: (name: string, opts?: unknown) =>
            full ? full.channel(name, opts as never) : new DeferredChannel(name, opts, realtime),
        removeChannel: (channel: unknown) =>
            channel instanceof DeferredChannel
                ? channel.detach()
                : loadSupabaseExtras().then((c) => c.removeChannel(channel as never)),
        removeAllChannels: () => (full ? full.removeAllChannels() : Promise.resolve([])),
        getChannels: () => (full ? full.getChannels() : []),
    };
    return new Proxy(target, {
        get(t, prop) {
            if (typeof prop !== 'string') return undefined;
            if (prop in t) return t[prop];
            // Loud only for real SupabaseClient members the student build leaves out; anything else (then, toJSON,
            // $$typeof, test matchers, DevTools) reads as undefined like on any plain object.
            if (NOT_CARRIED.has(prop)) {
                throw new Error(`[supabase-lite] "${prop}" is not part of the student client (spec 2026-10-14_student-lite-supabase-boot)`);
            }
            return undefined;
        },
    }) as unknown as SupabaseClient;
}

const DatabaseModule: DatabaseModuleType = {
    client: null,
    _initPromise: null,
    init(): Promise<SupabaseClient | null> {
        if (this.client) return Promise.resolve(this.client);
        if (window.supabaseClient) {
            this.client = window.supabaseClient;
            return Promise.resolve(this.client);
        }
        // No Zod here on purpose (decision T-5): zod is not in the boot chunk and would add ~12 KB gz to it; the
        // check is the same presence check `client.ts:62-77` makes for the other three apps.
        const url = import.meta.env.VITE_SUPABASE_URL as string;
        const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
        if (!url || !anonKey) {
            console.error('❌ Database: Missing environment variables: VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY. Reference: .env.example.');
            return Promise.resolve(null);
        }
        config = { url, anonKey };
        core = createLiteCore({ url, anonKey, storageKey: SUPABASE_STORAGE_KEY });
        this.client = facade(core, url);
        window.supabaseClient = this.client;
        return Promise.resolve(this.client);
    },
};

/** Synchronous accessor — use only after auth boot. */
export function getSupabaseClient(): SupabaseClient | null {
    return DatabaseModule.client || window.supabaseClient || null;
}

export async function waitForClient(): Promise<SupabaseClient | null> {
    return getSupabaseClient() ?? DatabaseModule.init();
}

export { DatabaseModule };
export type { DatabaseModuleType };
export default DatabaseModule;
